import { useId } from "react";
import { Link } from "react-router-dom";

import { useApiQuery, useHealth } from "../api/hooks";
import type { FilesStatus, MeView } from "../api/types";
import { useLive, type LiveStatus } from "../live/store";
import { Pill } from "../ui/Pill";
import { agentUnavailableSentence } from "../ui/text";
import { useMotionStore, type MotionSetting } from "./motion";

/** The sentence for the status of the written files (2.9.1 `FilesStatus`). */
export function filesStatusWords(status: FilesStatus): string {
  switch (status.status) {
    case "up_to_date":
      return "Files up to date";
    case "writing":
      return "Writing the files";
    case "check_failed":
      return "The file check failed";
    case "never_written":
      return "Files not written yet";
  }
}

/**
 * The status of the written files, from the live channel, or from the files view until the channel has spoken. `enabled` is
 * false for a person with no discipline: there are no files to read.
 */
export function useFilesStatus(enabled = true): FilesStatus | undefined {
  const live = useLive();
  const fromView = useApiQuery("files_view", {}, { staleTime: 5 * 60_000, retry: false, enabled });
  return live.files ?? fromView.data?.status;
}

/** The status of the written files as a pill that links to the files screen. */
export function FilesPill() {
  const status = useFilesStatus();
  if (!status) return null;
  const failed = status.status === "check_failed";
  return (
    <Link to="/files" className="files-pill" title={failed ? (status.message ?? undefined) : undefined}>
      <Pill tone={failed ? "problem" : "neutral"}>{filesStatusWords(status)}</Pill>
    </Link>
  );
}

/** What needs attention in the app, in words, for the mark on the account button: the back end cannot be reached, the file check failed. */
export function useAppProblems(me: MeView): string[] {
  const health = useHealth();
  const files = useFilesStatus(!!me.active);
  const problems: string[] = [];
  if (health.isError) problems.push("the back end cannot be reached");
  if (files?.status === "check_failed") problems.push("the file check failed");
  return problems;
}

const MOTION_WORDS: Record<MotionSetting, string> = {
  on: "On",
  off: "Off",
  system: "As my computer",
};

/** The switch that turns all motion off (ui-direction, visual language). */
export function MotionSwitch() {
  const id = useId();
  const setting = useMotionStore((state) => state.setting);
  const choose = useMotionStore((state) => state.choose);
  return (
    <span className="motion-switch">
      <label htmlFor={id}>Motion</label>{" "}
      <select id={id} value={setting} onChange={(event) => choose(event.target.value as MotionSetting)}>
        {(Object.keys(MOTION_WORDS) as MotionSetting[]).map((value) => (
          <option key={value} value={value}>
            {MOTION_WORDS[value]}
          </option>
        ))}
      </select>
    </span>
  );
}

const LIVE_WORDS: Record<LiveStatus, string> = {
  idle: "Live updates: not started",
  connecting: "Live updates: connecting",
  open: "Live updates: connected",
  reconnecting: "Live updates: reconnecting",
  closed: "Live updates: off",
};

/**
 * The facts about the app, as a short list: the back end, the agent, the library index, the live channel. They are in the
 * account menu of the bar (the screen has no foot any more).
 */
export function StatusFacts({ me }: { me: MeView }) {
  const health = useHealth();
  const live = useLive();
  const data = health.data;
  const agent = me.agent;
  return (
    <ul className="status-facts" aria-label="Status">
      {health.isPending && <li>Back end: checking…</li>}
      {health.isError && <li>Back end: not reachable</li>}
      {data && <li>Back end: connected, version {data.version}</li>}
      {!agent.available && <li>{agentUnavailableSentence(agent.reason)}</li>}
      {agent.available && agent.demo && <li>The agent is in demo mode</li>}
      {agent.available && !agent.demo && <li>The agent is ready{agent.uses_own_key ? " (with your own key)" : ""}</li>}
      {data && !data.index_built && <li>The library index is not built yet.</li>}
      {me.active && (
        <li role="status" data-live={live.status}>
          {LIVE_WORDS[live.status]}
        </li>
      )}
    </ul>
  );
}
